import { useState, useEffect, useCallback, useMemo } from "react";
import { formatPlAmount } from "../utils/formatPlAmount";
import type { NeedsYourAttentionEntity } from "../../domain/entities/NeedsYourAttentionEntity";
import { useNavigate } from "react-router-dom";
import {
  getClientOverviewBootstrapData,
  getClientOverviewBootstrapError,
  getClientOverviewBootstrapPromise,
} from "../state/clientOverviewBootstrapStore";
import { useClientOverviewBootstrap } from "./useClientOverviewBootstrap";

export interface NeedsYourAttentionItem {
  variant: "error" | "brand";
  title: string;
  description: string;
  actionLabel: string;
  onAction?: () => void;
}

function entityToCardItems(
  entity: NeedsYourAttentionEntity,
  actions: {
    goToArAging: () => void;
    goToApAging: () => void;
    goToCompliance: () => void;
    goToQueries: () => void;
    goToCashAndBank: () => void;
  },
): NeedsYourAttentionItem[] {
  const items: NeedsYourAttentionItem[] = [];

  // Overdue invoices (overdue amount/count only) (overdue amount/count only)
  if (entity.overdueInvoices.invoiceCount > 0) {
    const n = entity.overdueInvoices.invoiceCount;
    const c = entity.overdueInvoices.customerCount;
    items.push({
      variant: "error",
      title: `${n} invoice${n === 1 ? "" : "s"} overdue`,
      description: `Total overdue: ${formatPlAmount(entity.overdueInvoices.totalOverdueAmount)} across ${c} customer${c === 1 ? "" : "s"}`,
      actionLabel: "View aging",
      onAction: actions.goToArAging,
    });
  }

  // Overdue bills (overdue amount/count only)
  if (entity.overdueBills.billCount > 0) {
    const n = entity.overdueBills.billCount;
    const v = entity.overdueBills.vendorCount;
    items.push({
      variant: "error",
      title: `${n} bill${n === 1 ? "" : "s"} overdue`,
      description: `Total overdue: ${formatPlAmount(entity.overdueBills.totalOverdueAmount)} across ${v} vendor${v === 1 ? "" : "s"}`,
      actionLabel: "View aging",
      onAction: actions.goToApAging,
    });
  }

  // Oldest overdue compliance
  if (entity.oldestOverdueCompliance) {
    const c = entity.oldestOverdueCompliance;
    const days = c.daysOverdue;
    items.push({
      variant: "error",
      title: `${c.complianceFormName} overdue by ${days} day${days === 1 ? "" : "s"}`,
      description: c.description,
      actionLabel: "View compliance",
      onAction: actions.goToCompliance,
    });
  }

  // Queries assigned to me
  if (entity.queriesAssignedToMe.count > 0) {
    const n = entity.queriesAssignedToMe.count;
    const old = entity.queriesAssignedToMe.oldestDays;
    items.push({
      variant: "brand",
      title: `${n} quer${n === 1 ? "y" : "ies"} awaiting your response`,
      description:
        old != null
          ? `Oldest query is ${old} day${old === 1 ? "" : "s"} old from your accountant`
          : `${n} open quer${n === 1 ? "y" : "ies"}`,
      actionLabel: "Respond",
      onAction: actions.goToQueries,
    });
  }

  // Uncategorized bank transactions (one card per bank or one combined)
  // if (entity.uncategorizedBankTransactions.length > 0) {
  //   const total = entity.uncategorizedBankTransactions.reduce(
  //     (s, b) => s + b.count,
  //     0,
  //   );
  //   if (entity.uncategorizedBankTransactions.length === 1) {
  //     const b = entity.uncategorizedBankTransactions[0];
  //     items.push({
  //       variant: "brand",
  //       title: "Bank reconciliation pending",
  //       description: `${b.count} unreconciled transaction${b.count === 1 ? "" : "s"} in ${b.bankName} account`,
  //       actionLabel: "View bank transactions",
  //       onAction: actions.goToCashAndBank,
  //     });
  //   } else {
  //     items.push({
  //       variant: "brand",
  //       title: "Bank reconciliation pending",
  //       description: `${total} unreconciled transactions across ${entity.uncategorizedBankTransactions.length} bank accounts`,
  //       actionLabel: "View bank transactions",
  //       onAction: actions.goToCashAndBank,
  //     });
  //   }
  // }

  return items;
}

export function useNeedsYourAttention(): {
  items: NeedsYourAttentionItem[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
} {
  useClientOverviewBootstrap();

  const navigate = useNavigate();

  const actions = useMemo(
    () => ({
      goToArAging: () => navigate("/books/ar-aging"),
      goToApAging: () => navigate("/books/ap-aging"),
      goToCompliance: () => navigate("/client/compliance"),
      goToQueries: () => navigate("/client/queries"),
      goToCashAndBank: () => navigate("/cash-and-bank"),
    }),
    [navigate],
  );

  const [items, setItems] = useState<NeedsYourAttentionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchNeedsYourAttention = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      let bootstrapEntity = getClientOverviewBootstrapData();
      if (!bootstrapEntity) {
        const bootstrapPromise = getClientOverviewBootstrapPromise();
        if (bootstrapPromise) {
          bootstrapEntity = await bootstrapPromise;
        }
      }

      const bootstrapError = getClientOverviewBootstrapError();
      if (!bootstrapEntity) {
        throw new Error(
          bootstrapError ?? "Overview bootstrap data unavailable",
        );
      }

      const section = bootstrapEntity.sections.needsYourAttention;
      if (section.status !== "ok" || !section.data) {
        throw new Error(section.error ?? "Needs your attention unavailable");
      }
      const entity = section.data;

      setItems(entityToCardItems(entity, actions));
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Failed to load needs your attention";
      setError(message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [actions]);

  useEffect(() => {
    fetchNeedsYourAttention();
  }, [fetchNeedsYourAttention]);

  return {
    items,
    loading,
    error,
    refetch: fetchNeedsYourAttention,
  };
}
